if (!customElements.get('vehicle-filters')) {
  class VehicleFilters extends HTMLElement {
    connectedCallback() {
      this.collectionUrl = this.dataset.collectionUrl;
      this.form = this.querySelector('form');
      this.makeSelect = this.querySelector('[data-vehicle-filter="make"]');
      this.modelSelect = this.querySelector('[data-vehicle-filter="model"]');
      this.yearSelect = this.querySelector('[data-vehicle-filter="year"]');
      this.clearButton = this.querySelector('[data-vehicle-clear]');
      this.status = this.querySelector('.vehicle-filters__status');
      this.productItems = Array.from(this.closest('.products').querySelectorAll('[data-vehicle-product]'));
      this.emptyState = this.closest('.products').querySelector('[data-vehicle-empty]');
      this.universalProducts = this.closest('.products').querySelector('[data-vehicle-universal]');

      try {
        this.index = JSON.parse(this.querySelector('[data-vehicle-index]').textContent)
          .filter((item) => item.make && item.model && Array.isArray(item.years));
      } catch (error) {
        this.index = [];
      }

      this.onChange = this.onChange.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.form.addEventListener('change', this.onChange);
      this.clearButton.addEventListener('click', this.onClear);
      window.addEventListener('popstate', this.onPopState);

      this.restoreFromUrl();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onChange);
      this.clearButton?.removeEventListener('click', this.onClear);
      window.removeEventListener('popstate', this.onPopState);
    }

    unique(values, numeric = false) {
      const uniqueValues = [...new Set(values.filter(Boolean))];
      return uniqueValues.sort(numeric
        ? (a, b) => Number(b) - Number(a)
        : (a, b) => a.localeCompare(b));
    }

    replaceOptions(select, values, selectedValue) {
      const firstOption = select.options[0];
      select.replaceChildren(firstOption);

      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.append(option);
      });

      select.value = values.includes(selectedValue) ? selectedValue : '';
    }

    rebuildOptions(requested = {}) {
      const makes = this.unique(this.index.map((item) => item.make));
      this.replaceOptions(this.makeSelect, makes, requested.make ?? this.makeSelect.value);

      const selectedMake = this.makeSelect.value;
      const models = selectedMake
        ? this.unique(this.index.filter((item) => item.make === selectedMake).map((item) => item.model))
        : [];
      this.replaceOptions(this.modelSelect, models, requested.model ?? this.modelSelect.value);
      this.modelSelect.disabled = !selectedMake;

      const selectedModel = this.modelSelect.value;
      const years = selectedMake && selectedModel
        ? this.unique(
          this.index
            .filter((item) => item.make === selectedMake && item.model === selectedModel)
            .flatMap((item) => item.years.map(String)),
          true
        )
        : [];
      this.replaceOptions(this.yearSelect, years, String(requested.year ?? this.yearSelect.value ?? ''));
      this.yearSelect.disabled = !selectedModel;
    }

    restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      this.rebuildOptions({
        make: params.get('vehicle_make') || '',
        model: params.get('vehicle_model') || '',
        year: params.get('vehicle_year') || ''
      });
      this.applyFilters(false);
    }

    onChange(event) {
      const role = event.target.dataset.vehicleFilter;
      if (!role) return;

      if (role === 'make') {
        this.modelSelect.value = '';
        this.yearSelect.value = '';
      } else if (role === 'model') {
        this.yearSelect.value = '';
      }

      this.rebuildOptions();
      this.applyFilters(true);
    }

    onClear() {
      this.makeSelect.value = '';
      this.rebuildOptions({ make: '', model: '', year: '' });
      this.applyFilters(true);
    }

    onPopState() {
      this.restoreFromUrl();
    }

    applyFilters(updateHistory) {
      const make = this.makeSelect.value;
      const model = this.modelSelect.value;
      const year = this.yearSelect.value;
      const hasSelection = Boolean(make || model || year);
      let visibleCount = 0;

      this.productItems.forEach((item) => {
        const itemYears = (item.dataset.vehicleYears || '').split(',').filter(Boolean);
        const isUniversal = item.dataset.vehicleUniversalFitment === 'true';
        const matchesVehicle = (!make || item.dataset.vehicleMake === make)
          && (!model || item.dataset.vehicleModel === model)
          && (!year || itemYears.includes(year));
        const matches = isUniversal || matchesVehicle;

        item.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (this.emptyState) this.emptyState.hidden = visibleCount > 0;
      if (this.universalProducts) this.universalProducts.hidden = !hasSelection;
      this.clearButton.hidden = !hasSelection;
      this.status.textContent = `${visibleCount} products`;

      if (updateHistory) {
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        params.delete('vehicle_make');
        params.delete('vehicle_model');
        params.delete('vehicle_year');

        if (make) params.set('vehicle_make', make);
        if (model) params.set('vehicle_model', model);
        if (year) params.set('vehicle_year', year);

        const query = params.toString();
        history.pushState({}, '', `${this.collectionUrl}${query ? `?${query}` : ''}`);
      }
    }
  }

  customElements.define('vehicle-filters', VehicleFilters);
}
